import { POSTHOG_PROVIDER_OPTIONS } from "@/config/posthog.config";
import posthog, { CaptureResult } from "posthog-js";

// initializes PostHog with minimal tracking options
const tryInitMinimalTracking = (): Promise<boolean> =>
    !posthog.__loaded
        ? new Promise<boolean>((resolve, reject) => {
              // skip if already loaded
              if (posthog.__loaded) return resolve(true);

              try {
                  posthog.init(POSTHOG_PROVIDER_OPTIONS.apiKey!, {
                      ...POSTHOG_PROVIDER_OPTIONS.options,
                      autocapture: false,
                      capture_pageview: false,
                      disable_session_recording: true,
                      persistence: "memory",
                  });

                  resolve(true);
              } catch (error) {
                  console.error("Error initializing PostHog:", error);
                  reject(false);
              }
          })
        : Promise.resolve(true);

// temporarily opts in to capturing if user has not opted in, captures event, then cleans up
interface TemporaryCaptureParams {
    event_name: string;
    properties?: Record<string, any> | null;
    options?: Record<string, any>;
    userIdentifier?: string;
    allowTemporaryOptIn: boolean;
}

const capture = async ({
    event_name,
    properties,
    options,
    userIdentifier,
    allowTemporaryOptIn,
}: TemporaryCaptureParams): Promise<CaptureResult | undefined> => {
    const isPostHogInitialized = await tryInitMinimalTracking();
    if (!isPostHogInitialized) throw new Error("PostHog failed to initialize.");

    const userNotOptedIn = !posthog.has_opted_in_capturing() || posthog.has_opted_out_capturing();

    // do not capture if user has not opted in and temporary opt-in is not allowed
    if (userNotOptedIn && !allowTemporaryOptIn) {
        console.debug(`User has not opted in to tracking and temporary opt-in is not allowed.`);
        return;
    }

    // allow posthog temporary capture, then clean up
    if (userNotOptedIn) posthog.opt_in_capturing();

    // identifies user by provided string if given
    if (userIdentifier) {
        posthog.identify(userIdentifier);
    }

    // capture event
    const captureResponse = posthog.capture(event_name, properties, options);

    // clean up capture if temporarily opted in
    if (userNotOptedIn) {
        posthog.opt_out_capturing();
        posthog.reset();
    }

    return captureResponse;
};

export const PosthogHelper = {
    capture,
};
